import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import ResponseModal from "@/components/ResponseModal";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Globe, AlertCircle, Share2, Check, Copy } from "lucide-react";

const ENGINE_LABEL = {
  openai: "GPT",
  anthropic: "Claude",
  gemini: "Gemini",
  perplexity: "Pplx*",
  google_aio: "AIO*",
};

const SENT_COLOR = {
  positive: "bg-emerald-100 text-emerald-800 border-emerald-300",
  neutral: "bg-zinc-100 text-zinc-800 border-zinc-300",
  negative: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function GeoMatrix() {
  const { current, projects, selectProject } = useProject();
  const [languages, setLanguages] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/languages").then((r) => setLanguages(r.data));
  }, []);

  useEffect(() => {
    if (!current) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/projects/${current.project_id}/matrix`);
        setMatrix(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [current]);

  const langLabel = useMemo(() => {
    const m = {};
    languages.forEach((l) => (m[l.code] = l.label));
    return m;
  }, [languages]);

  const openResponse = async (cell, prompt, language, engine) => {
    if (!cell?.result_id) return;
    try {
      const r = await api.get(`/run-results/${cell.result_id}`);
      setResp({
        ...r.data,
        prompt_text: `${prompt.text} · ${langLabel[language] || language} · ${ENGINE_LABEL[engine] || engine}`,
      });
    } catch {
      /* ignore */
    }
  };

  const hasRun = !!matrix?.run_id;
  const hasMultiLang = (matrix?.languages || []).length > 1;

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">
            <Globe size={10} className="inline -mt-0.5 mr-1" /> Geo matrix
          </div>
          <h1 className="display mt-2 text-4xl font-black tracking-tighter">
            Prompts × languages × engines
          </h1>
          <p className="mt-2 text-zinc-600 text-sm max-w-xl">
            One cell per (prompt, language, engine). Colour = sentiment. ✓ = you
            were mentioned, ✗ = you were not.
          </p>
        </div>
        {hasRun && current && (
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={async () => {
                setPublishing(true);
                setPublishError(null);
                try {
                  const r = await api.post("/atlas/publish", {
                    project_id: current.project_id,
                  });
                  const url = `${window.location.origin}/atlas/${r.data.country_slug}/${r.data.slug}`;
                  setPublished(url);
                } catch (err) {
                  setPublishError(
                    err?.response?.data?.detail || "Could not publish atlas"
                  );
                } finally {
                  setPublishing(false);
                }
              }}
              disabled={publishing}
              data-testid="publish-atlas"
              className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-3.5 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              <Share2 size={13} />
              {publishing ? "Publishing…" : "Publish as Atlas"}
            </button>
            {published && (
              <div
                className="flex items-center gap-2 text-xs"
                data-testid="atlas-published"
              >
                <a
                  href={published}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-zinc-700 truncate max-w-[260px]"
                >
                  {published}
                </a>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(published);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  data-testid="atlas-copy"
                  className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-0.5 rounded-[4px] hover:bg-zinc-100"
                >
                  {copied ? <Check size={10} /> : <Copy size={10} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            )}
            {publishError && (
              <div
                className="text-xs text-rose-600"
                data-testid="atlas-publish-error"
              >
                {publishError}
              </div>
            )}
          </div>
        )}
      </div>

      {!hasMultiLang && (
        <div
          className="mb-4 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-[4px]"
          data-testid="matrix-single-lang-notice"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          Enable more languages in <a href="/dashboard/settings" className="underline">Settings</a> to unlock the full matrix. You're currently tracking only {(matrix?.languages || ["en-GB"])[0]}.
        </div>
      )}

      {loading ? (
        <div className="card-flat p-8 text-center text-sm text-zinc-500">Loading matrix…</div>
      ) : !hasRun ? (
        <div className="card-flat p-8 text-center text-sm text-zinc-500">
          No completed runs yet. Trigger a run from Overview to populate the matrix.
        </div>
      ) : (
        <section className="card-flat overflow-hidden" data-testid="matrix-table">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#fafafa] border-b border-zinc-200">
                <tr>
                  <th className="sticky left-0 bg-[#fafafa] text-left p-3 label-eyebrow w-[320px] min-w-[320px] z-10 border-r border-zinc-200">
                    Prompt
                  </th>
                  {matrix.languages.map((lang) => (
                    <th
                      key={lang}
                      colSpan={matrix.engines.length}
                      className="text-center p-2 label-eyebrow border-r border-zinc-200"
                    >
                      {langLabel[lang] || lang}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th className="sticky left-0 bg-[#fafafa] p-3 z-10 border-r border-zinc-200" />
                  {matrix.languages.map((lang) =>
                    matrix.engines.map((eng) => (
                      <th
                        key={`${lang}-${eng}`}
                        className="text-center py-2 text-[10px] text-zinc-500 mono uppercase tracking-[0.14em] w-[72px] min-w-[72px]"
                      >
                        {ENGINE_LABEL[eng] || eng}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {matrix.prompts.map((prompt, i) => (
                  <tr
                    key={prompt.prompt_id}
                    className={i % 2 === 0 ? "bg-white" : "bg-[#fafafa]/30"}
                  >
                    <td
                      className={`sticky left-0 p-3 border-r border-zinc-200 max-w-[320px] z-10 ${
                        i % 2 === 0 ? "bg-white" : "bg-[#fcfcfc]"
                      }`}
                    >
                      <div className="text-xs font-medium truncate" title={prompt.text}>
                        {prompt.text}
                      </div>
                      <div className="mt-0.5 mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                        {prompt.category.replace("_", " ")}
                      </div>
                    </td>
                    {matrix.languages.map((lang) =>
                      matrix.engines.map((eng) => {
                        const cell = matrix.cells?.[prompt.prompt_id]?.[lang]?.[eng];
                        return (
                          <td
                            key={`${prompt.prompt_id}-${lang}-${eng}`}
                            className="text-center p-1"
                          >
                            <Cell
                              cell={cell}
                              onOpen={() =>
                                openResponse(cell, prompt, lang, eng)
                              }
                              testid={`cell-${prompt.prompt_id}-${lang}-${eng}`}
                            />
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-zinc-200 p-3 flex items-center gap-3 text-[11px] text-zinc-500">
            <Legend swatch="bg-emerald-100 border-emerald-300" label="Positive" />
            <Legend swatch="bg-zinc-100 border-zinc-300" label="Neutral" />
            <Legend swatch="bg-rose-100 border-rose-300" label="Negative" />
            <Legend swatch="bg-white border-zinc-200" label="Not mentioned" />
            <span className="ml-auto mono">
              Run {matrix.run_id?.slice(0, 10)} ·{" "}
              {matrix.run_at
                ? new Date(matrix.run_at).toLocaleString()
                : ""}
            </span>
          </div>
        </section>
      )}

      <ResponseModal response={resp} onClose={() => setResp(null)} />
    </DashboardShell>
  );
}

function Cell({ cell, onOpen, testid }) {
  if (!cell) {
    return (
      <span
        className="inline-block w-8 h-8 rounded-[3px] border border-zinc-200 bg-white text-zinc-300 text-xs leading-8"
        data-testid={testid}
        aria-label="no data"
      >
        ·
      </span>
    );
  }
  if (cell.error) {
    return (
      <span
        className="inline-block w-8 h-8 rounded-[3px] border border-rose-300 bg-rose-50 text-rose-700 text-xs leading-8"
        title={cell.error}
        data-testid={testid}
      >
        !
      </span>
    );
  }
  if (!cell.mentioned_brand) {
    return (
      <button
        onClick={onOpen}
        data-testid={testid}
        className="inline-block w-8 h-8 rounded-[3px] border border-zinc-200 bg-white text-zinc-400 text-xs leading-8 hover:bg-zinc-100 transition-colors"
      >
        ✗
      </button>
    );
  }
  const cls = SENT_COLOR[cell.sentiment] || SENT_COLOR.neutral;
  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      className={`inline-block w-8 h-8 rounded-[3px] border ${cls} text-xs leading-8 font-semibold hover:brightness-95 transition-all`}
    >
      ✓
    </button>
  );
}

function Legend({ swatch, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-3 h-3 rounded-sm border ${swatch}`} />
      {label}
    </span>
  );
}
